import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { printService } from 'api/services/printService';
import type { PrintJob, PrintJobQuery, PrintJobStatusFilter, PrinterLookup } from 'api/types';
import PrintReasonModal from 'components/modals/modules/print-module/PrintReasonModal';
import PrintReroutePickerModal from 'components/modals/modules/print-module/PrintReroutePickerModal';
import PrintJobDetailModal from 'components/modals/modules/print-module/PrintJobDetailModal';
import { Chip, JobStatusChip, JOB_STATUS_LABEL, fmtTime, currentOutletId, currentOutletName, apiMessage } from './printUi';

/**
 * Print Queue (KOT printing, architecture section 17): every print job, live.
 * Opens on the jobs not yet handled for the outlet in the header. Retry a
 * failed job, print it elsewhere, cancel it, or mark a failure handled once
 * the ticket reached the kitchen another way. Refreshes every 15 s, like the
 * Bill Queue.
 *
 * Paged, searched and filtered on the server.
 */

const POLL_MS = 15_000;
const STATUS_OPTIONS: { value: PrintJobStatusFilter; label: string }[] = [
  { value: 'OPEN', label: 'Open (not handled yet)' },
  { value: 'ALL', label: 'All' },
  ...(['QUEUED', 'CLAIMED', 'SENT', 'PRINTED', 'FAILED', 'REROUTED', 'CANCELLED'] as const).map((s) => ({ value: s, label: JOB_STATUS_LABEL[s] })),
];

interface Filters { status: PrintJobStatusFilter; printerId?: string; from?: string; to?: string; allOutlets?: boolean }
const DEFAULT_FILTERS: Filters = { status: 'OPEN' };

/** A date input's day as the start / end of that day in the browser's zone, ISO with offset. */
const dayStart = (d?: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd = (d?: string) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);

type Pending = { job: PrintJob; action: 'cancel' | 'resolve' | 'retry' | 'reroute' } | null;

const PrintQueueScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  // Read on mount: an outlet switch in the header remounts the Printing screens
  // (main_layout keys them by outlet), so this always holds the tab's outlet.
  const outletId = useMemo(currentOutletId, []);
  const outletName = useMemo(currentOutletName, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [draft, setDraft] = useState<Filters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState<PrintJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [printers, setPrinters] = useState<PrinterLookup[]>([]);

  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const firstLoad = useRef(true);
  // Each request takes a number; only the latest may write the grid, so a slow
  // answer for an old filter / page (or a poll) never overwrites a newer one.
  const requestSeq = useRef(0);

  // The panel always shows what is applied (the quick status chips change it too).
  useEffect(() => { setDraft(filters); }, [filters]);

  const canAct = allowedActions.includes('update') || allowedActions.includes('manage') || allowedActions.includes('edit');

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchQuery.trim() || undefined); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => { printService.getPrinterLookup().then(setPrinters).catch(() => setPrinters([])); }, []);

  const query = useMemo((): PrintJobQuery => ({
    status: filters.status,
    printerId: filters.printerId,
    scopeId: filters.allOutlets || !outletId ? undefined : outletId,
    from: dayStart(filters.from),
    to: dayEnd(filters.to),
    search,
    page,
    pageSize,
  }), [filters, search, page, pageSize, outletId]);

  // A poll refreshes quietly: the grid only shows its spinner on the first load and on a new query.
  const load = useCallback(async (quiet = false) => {
    const seq = ++requestSeq.current;
    if (!quiet) setLoading(true);
    try {
      const r = await printService.getJobs(query);
      if (seq !== requestSeq.current) return;
      setRows(r.data);
      setTotal(r.pagination.totalItems);
      setLoadError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      if (!quiet) { setRows([]); setTotal(0); }
      setLoadError(apiMessage(err, 'The print queue could not be loaded.'));
    } finally {
      if (seq === requestSeq.current) { setLoading(false); firstLoad.current = false; }
    }
  }, [query]);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => { void load(true); }, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) { notificationService.toast.warning('No data to export'); return; }
    const headers = ['Time', 'Reference', 'Kitchen station', 'Printer', 'Original printer', 'Copy', 'Status', 'Attempts', 'Last error', 'Requested by', 'Handled'];
    const lines = rows.map((r) => [r.createdAt, r.sourceReference ?? r.documentType, r.sourceLabel ?? '', r.printerName ?? '', r.originalPrinterName ?? '',
      r.copyKind === 'REPRINT' ? `Reprint ${r.reprintNumber ?? ''}` : `${r.copyNumber}/${r.copyCount}`, r.jobStatus, r.attempts, r.lastError ?? '',
      r.requestedByLabel ?? '', r.resolvedAt ?? ''].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const url = URL.createObjectURL(new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `print-queue-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const run = useCallback(async (arg: string) => {
    if (!pending) return;
    const { job, action } = pending;
    setBusy(true); setActionError(null);
    try {
      if (action === 'retry') await printService.retryJob(job.id);
      else if (action === 'reroute') await printService.rerouteJob(job.id, arg);
      else if (action === 'cancel') await printService.cancelJob(job.id, arg);
      else await printService.resolveJob(job.id, arg || undefined);
      const ref = job.sourceReference ?? 'The job';
      notificationService.toast.success({
        retry: `${ref} queued again.`,
        reroute: `${ref} sent to ${printers.find((p) => p.id === arg)?.printerName ?? 'the other printer'}.`,
        cancel: `${ref} cancelled.`,
        resolve: `${ref} marked handled.`,
      }[action]);
      setPending(null);
      void load(true);
    } catch (err) {
      setActionError(apiMessage(err, 'That could not be done.'));
    } finally { setBusy(false); }
  }, [pending, printers, load]);

  const columns = useMemo((): SyutGridColumn<PrintJob>[] => [
    { id: 'createdAt', header: 'Time', accessorKey: 'createdAt', enableSorting: true, size: 120, cell: ({ row }) => fmtTime(row.original.createdAt) },
    { id: 'sourceReference', header: 'Ticket', accessorKey: 'sourceReference', enableSorting: true, size: 130,
      cell: ({ row }) => <strong>{row.original.sourceReference ?? (row.original.documentType === 'TEST' ? 'Test page' : row.original.documentType)}</strong> },
    { id: 'sourceLabel', header: 'Kitchen station', accessorKey: 'sourceLabel', enableSorting: true, size: 150, cell: ({ row }) => row.original.sourceLabel ?? '—' },
    { id: 'printerName', header: 'Printer', accessorKey: 'printerName', enableSorting: true, size: 180,
      cell: ({ row }) => (
        <span>{row.original.printerName ?? '—'}
          {row.original.originalPrinterName && <span className="prn-sub">from {row.original.originalPrinterName}</span>}
          {!row.original.agentOnline && row.original.agentName && ['QUEUED', 'CLAIMED'].includes(row.original.jobStatus) && <span className="prn-sub">agent offline</span>}
        </span>
      ) },
    { id: 'copy', header: 'Copy', accessorKey: 'copyNumber', enableSorting: false, size: 150,
      cell: ({ row }) => {
        const j = row.original;
        return (
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {j.copyKind === 'REPRINT' && <Chip tone="info" title={j.requestReason}>Reprint {j.reprintNumber ?? ''}</Chip>}
            {j.copyKind === 'REROUTED' && <Chip tone="warn">Rerouted</Chip>}
            {j.copyKind === 'ORIGINAL' && j.copyCount > 1 && <Chip tone="muted">Copy {j.copyNumber}/{j.copyCount}</Chip>}
            {j.possibleDuplicate && <Chip tone="warn" title="Re-sent after a lost result: printed DUPLICATE">Duplicate</Chip>}
            {j.copyKind === 'ORIGINAL' && j.copyCount <= 1 && !j.possibleDuplicate && <span style={{ color: 'var(--text-muted)' }}>—</span>}
          </span>
        );
      } },
    { id: 'jobStatus', header: 'Status', accessorKey: 'jobStatus', enableSorting: true, size: 140,
      cell: ({ row }) => <span data-job-status={row.original.jobStatus}><JobStatusChip job={row.original} /></span> },
    { id: 'attempts', header: 'Attempts', accessorKey: 'attempts', enableSorting: true, size: 90 },
    { id: 'lastError', header: 'Last error', accessorKey: 'lastError', enableSorting: false, size: 220,
      cell: ({ row }) => (row.original.lastError
        ? <span style={{ color: 'var(--danger, #dc2626)' }} title={row.original.lastError}>{row.original.lastError}</span>
        : <span style={{ color: 'var(--text-muted)' }}>—</span>) },
    { id: 'requestedByLabel', header: 'Requested by', accessorKey: 'requestedByLabel', enableSorting: true, size: 140, cell: ({ row }) => row.original.requestedByLabel ?? '—' },
  ], []);

  const getRowActions = useCallback((row: PrintJob): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'Details and history', icon: <IconSystem.eye size={16} />, onClick: () => setDetailId(row.id) },
    ];
    if (!canAct) return items;
    const s = row.jobStatus;
    const ask = (action: NonNullable<Pending>['action']) => () => { setActionError(null); setPending({ job: row, action }); };
    if (s === 'FAILED') items.push({ id: 'retry', label: 'Retry', icon: <IconSystem.refresh size={16} />, onClick: ask('retry') });
    if (s === 'FAILED' || s === 'QUEUED') items.push({ id: 'reroute', label: 'Print elsewhere…', icon: <IconSystem.print size={16} />, onClick: ask('reroute') });
    if ((s === 'FAILED' || s === 'REROUTED') && !row.resolvedAt) items.push({ id: 'resolve', label: 'Mark handled…', icon: <IconSystem.checkCircle size={16} />, onClick: ask('resolve'),
      description: 'The ticket reached the kitchen another way; clears the alert.' });
    if (s === 'FAILED' || s === 'QUEUED') items.push({ id: 'cancel', label: 'Cancel…', icon: <IconSystem.xCircle size={16} />, onClick: ask('cancel'), variant: 'danger' });
    return items;
  }, [canAct]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh data', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' },
  ], [load, handleExport]);

  const printerOptions = useMemo(() => printers.map((p) => ({ value: p.id, label: p.printerName })), [printers]);
  const scopeText = filters.allOutlets || !outletId ? 'all outlets' : (outletName || 'this outlet');
  const p = pending;

  return (
    <div className="module-master-container" data-screen="print-queue">
      <ModuleHeader
        title="Print Queue"
        description={`Print jobs for ${scopeText}: ${STATUS_OPTIONS.find((o) => o.value === filters.status)?.label.toLowerCase()}. Refreshes every 15 seconds`}
        icon={<IconSystem.list size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search KOT number, station or printer' }}
        filterConfig={{ onClick: () => { setDraft(filters); setFilterOpen(!filterOpen); }, isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div data-quick-status style={{ padding: '0 12px 6px', display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
              {(['OPEN', 'FAILED', 'QUEUED', 'ALL'] as PrintJobStatusFilter[]).map((s) => (
                <button key={s} type="button" className={`btn ${filters.status === s ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '2px 10px', fontSize: '0.78rem' }} data-status-chip={s}
                  onClick={() => { setFilters((f) => ({ ...f, status: s })); setPage(1); }}>
                  {s === 'OPEN' ? 'Open' : s === 'ALL' ? 'All' : JOB_STATUS_LABEL[s as keyof typeof JOB_STATUS_LABEL]}
                </button>
              ))}
              {outletId && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8, fontSize: '0.8rem' }}>
                  <input type="checkbox" checked={!!filters.allOutlets} data-all-outlets
                    onChange={(e) => { setFilters((f) => ({ ...f, allOutlets: e.target.checked })); setPage(1); }} />
                  All outlets
                </label>
              )}
              {loadError && !loading && rows.length > 0 && <span style={{ color: 'var(--danger, #dc2626)', fontSize: '0.78rem' }}>Refresh failed, retrying</span>}
            </div>
            <SyutGrid data={rows} columns={columns} enableSorting enablePagination manualPagination totalCount={total}
              currentPage={page - 1} pageSize={pageSize} pageSizeOptions={[10, 25, 50, 100]}
              onPageChange={(i) => setPage(i + 1)} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              frozenRightColumns={['actions']} isLoading={loading} rowActions={getRowActions}
              emptyMessage={loadError ?? (filters.status === 'OPEN' ? 'Nothing waiting or failed. Every ticket printed.' : 'No print jobs found.')} />
          </div>
        </div>
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Status" options={STATUS_OPTIONS} value={draft.status} size="sm" isClearable={false} menuPortalTarget={document.body}
                onChange={(v) => setDraft((d) => ({ ...d, status: ((v as string) || 'OPEN') as PrintJobStatusFilter }))} />
            </div>
            <div className="filter-section">
              <SyutSelect label="Printer" options={printerOptions} value={draft.printerId ?? ''} placeholder="All printers" size="sm" isClearable
                menuPortalTarget={document.body} onChange={(v) => setDraft((d) => ({ ...d, printerId: (v as string) || undefined }))} />
            </div>
            <div className="filter-section">
              <label className="filter-section-title">Date range</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="date" className="form-input" value={draft.from ?? ''} data-filter-from
                  onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value || undefined }))} style={{ flex: 1, minWidth: 0, border: '1px solid var(--border-color)' }} />
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>to</span>
                <input type="date" className="form-input" value={draft.to ?? ''} data-filter-to
                  onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value || undefined }))} style={{ flex: 1, minWidth: 0, border: '1px solid var(--border-color)' }} />
              </div>
            </div>
            {outletId && (
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={!!draft.allOutlets} onChange={(e) => setDraft((d) => ({ ...d, allOutlets: e.target.checked }))} />
                  All outlets (not only {outletName || 'the header outlet'})
                </label>
              </div>
            )}
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => { setDraft(DEFAULT_FILTERS); setFilters(DEFAULT_FILTERS); setPage(1); }}>Clear</button>
            <button className="btn btn-primary" onClick={() => { setFilters(draft); setPage(1); setFilterOpen(false); }}>Apply</button>
          </div>
        </div>
      </div>

      <PrintJobDetailModal jobId={detailId} onClose={() => setDetailId(null)} />
      <ConfirmDialog open={p?.action === 'retry'} title={`Retry ${p?.job.sourceReference ?? 'the job'}?`}
        message={`It goes back in the queue for ${p?.job.printerName ?? 'its printer'} with a fresh set of attempts.${actionError ? `\n\n${actionError}` : ''}`}
        confirmLabel="Retry" cancelLabel="Cancel" loading={busy} loadingMessage="Queuing..."
        onConfirm={() => void run('')} onCancel={() => setPending(null)} />
      <PrintReroutePickerModal job={p?.action === 'reroute' ? p.job : null} busy={busy} error={actionError}
        onConfirm={(id) => void run(id)} onCancel={() => setPending(null)} />
      <PrintReasonModal isOpen={p?.action === 'cancel'} title={`Cancel ${p?.job.sourceReference ?? 'the job'}?`}
        message="The ticket will not print. Use this when the order changed or it was printed another way. A reason is required."
        confirmLabel="Cancel job" variant="danger" busy={busy} error={actionError}
        presets={['Order changed', 'Printed another way', 'Duplicate request']}
        onConfirm={(r) => void run(r)} onCancel={() => setPending(null)} />
      <PrintReasonModal isOpen={p?.action === 'resolve'} title={`Mark ${p?.job.sourceReference ?? 'the job'} handled?`}
        message="The kitchen has the ticket another way. It leaves the alerts and the open queue."
        confirmLabel="Mark handled" required={false} label="Note" placeholder="e.g., Read to the chef" busy={busy} error={actionError}
        presets={['Ticket reached the kitchen', 'Printed at the backup printer', 'Printed from the kitchen screen']}
        onConfirm={(r) => void run(r)} onCancel={() => setPending(null)} />
    </div>
  );
};

export default PrintQueueScreen;
